import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import SectionHeader from "../../components/ui/SectionHeader";
import ToggleSetting from "../../components/ui/ToggleSetting";
import { Button } from "../../components/ui/Button";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import { getAllForms } from "../../services/consultationform";
import {
  getCustomerRegistrationSettings,
  updateCustomerRegistrationSettings,
} from "../../services/customerRegistration";
import { Check, Copy, Link2, UserPlus } from "lucide-react";

type FormOption = { id: number; title: string };

const CustomerRegistrationSettings: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [linkedFormIds, setLinkedFormIds] = useState<number[]>([]);
  const [publicUrl, setPublicUrl] = useState("");
  const [forms, setForms] = useState<FormOption[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const [settings, formsRes] = await Promise.all([
          getCustomerRegistrationSettings(),
          getAllForms(),
        ]);

        if (!mounted) return;

        setEnabled(!!settings.enabled);
        setLinkedFormIds(
          Array.isArray(settings.linked_form_ids)
            ? settings.linked_form_ids.map(Number)
            : []
        );
        // Prefer current app origin so staging/local copy links work; fall back to API URL.
        const encoded = settings.encoded_company_id;
        setPublicUrl(
          encoded
            ? `${window.location.origin}/forms/register/${encoded}`
            : settings.public_url || ""
        );

        const raw = Array.isArray(formsRes)
          ? formsRes
          : formsRes?.data || formsRes?.forms || [];
        const mapped: FormOption[] = (raw as any[])
          .map((f) => ({
            id: Number(f.id),
            title: f.title || f.name || `Form #${f.id}`,
          }))
          .filter((f) => f.id > 0);
        setForms(mapped);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load customer registration settings");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCount = linkedFormIds.length;

  const toggleForm = (id: number) => {
    setLinkedFormIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateCustomerRegistrationSettings({
        enabled,
        linked_form_ids: linkedFormIds,
      });
      setEnabled(!!updated.enabled);
      setLinkedFormIds(
        Array.isArray(updated.linked_form_ids)
          ? updated.linked_form_ids.map(Number)
          : []
      );
      const encoded = updated.encoded_company_id;
      setPublicUrl(
        encoded
          ? `${window.location.origin}/forms/register/${encoded}`
          : updated.public_url || ""
      );
      toast.success("Customer registration settings saved");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to save settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Registration link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const sortedForms = useMemo(
    () => [...forms].sort((a, b) => a.title.localeCompare(b.title)),
    [forms]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading registration settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Form Settings"
        description="Share a public registration link and optionally send consultation forms after signup."
        variant="card"
      />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 space-y-6">
          <div>
            <SectionHeader title="Registration link" />
            <ToggleSetting
              label="Enable public registration"
              description="When enabled, anyone with the link can register as a customer."
              checked={enabled}
              onChange={setEnabled}
              showBackground
            />

            <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <Link2 size={16} className="text-[#885ABB]" />
                Public link
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  readOnly
                  value={publicUrl}
                  className="flex-1 h-10 rounded-lg border border-gray-200 bg-white px-3 text-xs sm:text-sm text-gray-700"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopy}
                  disabled={!publicUrl || !enabled}
                  className="h-10 shrink-0"
                >
                  {copied ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Check size={14} /> Copied
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <Copy size={14} /> Copy link
                    </span>
                  )}
                </Button>
              </div>
              {!enabled && (
                <p className="text-xs text-amber-700">
                  Enable registration to use this link. Visitors will see “not
                  enabled” until you turn it on.
                </p>
              )}
            </div>
          </div>

          <div>
            <SectionHeader title="Linked consultation forms" />
            <p className="text-sm text-gray-500 mb-3">
              After a customer registers, these forms are created and sent by
              email/SMS (same as Send Form). Selected: {selectedCount}
            </p>

            {sortedForms.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                No consultation forms found. Create forms from Consultations →
                Form Templates first.
              </p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {sortedForms.map((form) => {
                  const checked = linkedFormIds.includes(form.id);
                  return (
                    <label
                      key={form.id}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                        checked
                          ? "border-purple-200 bg-purple-50/60"
                          : "border-gray-100 hover:border-gray-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleForm(form.id)}
                        className="h-4 w-4 rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                      />
                      <span className="text-sm font-medium text-gray-800">
                        {form.title}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2 border-t border-gray-100">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#885ABB] hover:bg-[#7a4dad] text-white"
            >
              {saving ? (
                "Saving…"
              ) : (
                <span className="inline-flex items-center gap-2">
                  <UserPlus size={16} />
                  Save settings
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegistrationSettings;
